export enum ErrorType {
  // Network errors
  NETWORK_ERROR = 'network_error',
  API_ERROR = 'api_error',
  TIMEOUT_ERROR = 'timeout_error',
  
  // Authentication errors
  AUTH_ERROR = 'auth_error',
  PERMISSION_ERROR = 'permission_error',
  SESSION_EXPIRED = 'session_expired',
  
  // Database errors
  DB_CONNECTION_ERROR = 'db_connection_error',
  DB_CONSTRAINT_ERROR = 'db_constraint_error',
  DB_QUERY_ERROR = 'db_query_error',
  
  // Application errors
  VALIDATION_ERROR = 'validation_error',
  BUSINESS_LOGIC_ERROR = 'business_logic_error',
  STATE_ERROR = 'state_error',
  
  // UI errors
  RENDER_ERROR = 'render_error',
  COMPONENT_ERROR = 'component_error',
  FORM_ERROR = 'form_error',
  
  // System errors
  MEMORY_ERROR = 'memory_error',
  SCRIPT_ERROR = 'script_error',
  UNKNOWN_ERROR = 'unknown_error'
}

export interface ErrorClassification {
  type: ErrorType;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  isRetryable: boolean;
  userImpact: string;
  suggestedAction: string;
}

export interface Breadcrumb {
  timestamp: string;
  type: 'navigation' | 'click' | 'api' | 'console' | 'custom';
  category: string;
  message: string;
  level: 'info' | 'warning' | 'error';
  data?: Record<string, any>;
}

export interface JourneyEvent {
  type: 'page_view' | 'user_action' | 'api_call';
  page?: string;
  action?: string;
  target?: string;
  endpoint?: string;
  method?: string;
  status?: number;
  metadata?: any;
  timestamp: number;
}

/**
 * Classifies an error based on its properties
 */
export function classifyError(error: Error | any): ErrorClassification {
  // Handle Supabase/PostgreSQL errors
  if (error?.code) {
    const code = error.code.toString();
    
    // PostgreSQL error codes
    if (code.startsWith('23')) {
      return {
        type: ErrorType.DB_CONSTRAINT_ERROR,
        severity: 'medium',
        category: 'Database',
        isRetryable: false,
        userImpact: 'Unable to save data due to validation error',
        suggestedAction: 'Check the form data and try again'
      };
    }
    
    if (code === '42P01' || code === '42703') {
      return {
        type: ErrorType.DB_QUERY_ERROR,
        severity: 'high',
        category: 'Database',
        isRetryable: false,
        userImpact: 'Database structure issue',
        suggestedAction: 'Contact support'
      };
    }
    
    if (code === 'PGRST301') {
      return {
        type: ErrorType.AUTH_ERROR,
        severity: 'high',
        category: 'Authentication',
        isRetryable: false,
        userImpact: 'Authentication required',
        suggestedAction: 'Please sign in to continue'
      };
    }
  }
  
  // Network errors
  if (error?.message?.toLowerCase().includes('network') || error?.message?.toLowerCase().includes('fetch')) {
    return {
      type: ErrorType.NETWORK_ERROR,
      severity: 'medium',
      category: 'Network',
      isRetryable: true,
      userImpact: 'Connection issue',
      suggestedAction: 'Check your internet connection and try again'
    };
  }
  
  // Authentication errors
  if (error?.message?.toLowerCase().includes('auth') || error?.message?.toLowerCase().includes('unauthorized')) {
    return {
      type: ErrorType.AUTH_ERROR,
      severity: 'high',
      category: 'Authentication',
      isRetryable: false,
      userImpact: 'Authentication issue',
      suggestedAction: 'Please sign in again'
    };
  }
  
  // Permission errors
  if (error?.message?.toLowerCase().includes('permission') || error?.message?.toLowerCase().includes('forbidden')) {
    return {
      type: ErrorType.PERMISSION_ERROR,
      severity: 'medium',
      category: 'Authorization',
      isRetryable: false,
      userImpact: 'Access denied',
      suggestedAction: 'You do not have permission to perform this action'
    };
  }
  
  // React render errors
  if (error?.stack?.includes('react') && (error?.message?.includes('render') || error?.message?.includes('Component'))) {
    return {
      type: ErrorType.RENDER_ERROR,
      severity: 'high',
      category: 'UI',
      isRetryable: false,
      userImpact: 'Display error',
      suggestedAction: 'Refresh the page'
    };
  }
  
  // Form validation errors
  if (error?.message?.toLowerCase().includes('validation') || error?.message?.toLowerCase().includes('invalid')) {
    return {
      type: ErrorType.VALIDATION_ERROR,
      severity: 'low',
      category: 'Validation',
      isRetryable: false,
      userImpact: 'Invalid input',
      suggestedAction: 'Please check your input and try again'
    };
  }
  
  // Timeout errors
  if (error?.message?.toLowerCase().includes('timeout')) {
    return {
      type: ErrorType.TIMEOUT_ERROR,
      severity: 'medium',
      category: 'Network',
      isRetryable: true,
      userImpact: 'Request timeout',
      suggestedAction: 'The request took too long. Please try again'
    };
  }
  
  // Default unknown error
  return {
    type: ErrorType.UNKNOWN_ERROR,
    severity: 'medium',
    category: 'Unknown',
    isRetryable: false,
    userImpact: 'An unexpected error occurred',
    suggestedAction: 'Please try again or contact support if the issue persists'
  };
}

/**
 * Generate a fingerprint for error grouping
 */
export function generateErrorFingerprint(error: Error | any, classification: ErrorClassification): string {
  const parts: string[] = [
    classification.type,
    error?.name || 'Error',
    // Remove dynamic parts from message (numbers, IDs, etc.)
    (error?.message || '')
      .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, 'UUID')
      .replace(/\b\d+\b/g, 'NUM')
      .replace(/["'].*?["']/g, 'STR')
  ];
  
  // Add stack trace location if available
  if (error?.stack) {
    const stackLines = error.stack.split('\n');
    const firstAppLine = stackLines.find((line: string) => 
      line.includes('/src/') && !line.includes('node_modules')
    );
    if (firstAppLine) {
      const match = firstAppLine.match(/\/src\/(.+?):\d+:\d+/);
      if (match) {
        parts.push(match[1]);
      }
    }
  }
  
  // Create a simple hash
  const str = parts.join('|');
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  return Math.abs(hash).toString(36);
}